import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const DocumentPreview = ({ language, document, onClose }) => {
  if (!document) return null;

  const getName = () => {
    if (language === 'fr') return document?.nameFr;
    if (language === 'ar') return document?.nameAr;
    return document?.nameEn;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (language === 'ar') {
      return date?.toLocaleDateString('ar-MA');
    }
    return date?.toLocaleDateString('fr-MA');
  };

  const detailsLabel = {
    en: 'Document Details',
    fr: 'Détails du document',
    ar: 'تفاصيل المستند'
  };

  const typeLabel = {
    en: 'Type',
    fr: 'Type',
    ar: 'النوع'
  };

  const sizeLabel = {
    en: 'Size',
    fr: 'Taille',
    ar: 'الحجم'
  };

  const createdLabel = {
    en: 'Created',
    fr: 'Créé',
    ar: 'تاريخ الإنشاء'
  };

  const modifiedLabel = {
    en: 'Modified',
    fr: 'Modifié',
    ar: 'تاريخ التعديل'
  };

  const versionLabel = {
    en: 'Version',
    fr: 'Version',
    ar: 'الإصدار'
  };

  const ownerLabel = {
    en: 'Owner',
    fr: 'Propriétaire',
    ar: 'المالك'
  };

  const tagsLabel = {
    en: 'Tags',
    fr: 'Étiquettes',
    ar: 'العلامات'
  };

  const downloadLabel = {
    en: 'Download',
    fr: 'Télécharger',
    ar: 'تحميل'
  };

  const shareLabel = {
    en: 'Share',
    fr: 'Partager',
    ar: 'مشاركة'
  };

  const deleteLabel = {
    en: 'Delete',
    fr: 'Supprimer',
    ar: 'حذف'
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground truncate flex-1 mr-4">
            {getName()}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-muted rounded-lg p-8 flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <Icon name={document?.icon} size={80} className={document?.color} />
                  <p className="text-sm text-muted-foreground mt-4">
                    {language === 'fr' ? 'Aperçu du document' : language === 'ar' ? 'معاينة المستند' : 'Document Preview'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {document?.type} • {document?.size}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-4">
                  {detailsLabel?.[language]}
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {typeLabel?.[language]}
                    </p>
                    <p className="text-sm text-foreground font-medium">
                      {document?.type}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {sizeLabel?.[language]}
                    </p>
                    <p className="text-sm text-foreground font-medium">
                      {document?.size}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {createdLabel?.[language]}
                    </p>
                    <p className="text-sm text-foreground font-medium">
                      {formatDate(document?.createdDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {modifiedLabel?.[language]}
                    </p>
                    <p className="text-sm text-foreground font-medium">
                      {formatDate(document?.modifiedDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {versionLabel?.[language]}
                    </p>
                    <p className="text-sm text-foreground font-medium">
                      v{document?.version}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-2">
                  {ownerLabel?.[language]}
                </p>
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <Image
                    src={document?.ownerAvatar}
                    alt={document?.ownerAvatarAlt}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {document?.owner}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-2">
                  {tagsLabel?.[language]}
                </p>
                <div className="flex flex-wrap gap-2">
                  {document?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border flex-wrap">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            onClick={() => console.log('Download', document?.id)}
          >
            {downloadLabel?.[language]}
          </Button>
          <Button
            variant="outline"
            iconName="Share2"
            iconPosition="left"
            onClick={() => console.log('Share', document?.id)}
          >
            {shareLabel?.[language]}
          </Button>
          <Button
            variant="destructive"
            iconName="Trash2"
            iconPosition="left"
            onClick={() => console.log('Delete', document?.id)}
          >
            {deleteLabel?.[language]}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;